import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import rocketplanData from './data/rocketplan-articles.json';
import rocketcrmData from './data/rocketcrm-articles.json';
import { HelpCenterSidebar } from './HelpCenterSidebar/HelpCenterSidebar';
import { HelpCenterArticle } from './HelpCenterArticle/HelpCenterArticle';
import classes from './helpCenter.module.css';

interface Article {
  id: string;
  title: string;
  body: string;
}

interface Collection {
  id: string;
  name: string;
  description: string;
  children: Collection[];
  articles: Article[];
}

interface HelpData {
  collections: Collection[];
}

const dataMap: Record<string, HelpData> = {
  rocketplan: rocketplanData,
  rocketcrm: rocketcrmData,
};

const titleMap: Record<string, string> = {
  rocketplan: 'RocketPlan Help Center',
  rocketcrm: 'RocketCRM Help Center',
};

const switchMap: Record<string, { path: string; label: string }> = {
  rocketplan: { path: '/help/rocketcrm', label: 'RocketCRM Help' },
  rocketcrm: { path: '/help/rocketplan', label: 'RocketPlan Help' },
};

function getAllArticles(collections: Collection[]): Article[] {
  const articles: Article[] = [];
  function walk(colls: Collection[]) {
    colls.forEach((c) => {
      articles.push(...c.articles);
      walk(c.children);
    });
  }
  walk(collections);
  return articles;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
}

interface Props {
  product: 'rocketplan' | 'rocketcrm';
}

export function HelpCenter({ product }: Props) {
  const data = dataMap[product];
  const allArticles = useMemo(() => getAllArticles(data.collections), [data]);
  const allArticleIds = useMemo(() => new Set(allArticles.map((a) => a.id)), [allArticles]);

  // Pre-compute searchable text once
  const articleSearchText = useMemo(
    () =>
      new Map(
        allArticles.map((a) => [a.id, `${a.title} ${stripHtml(a.body)}`.toLowerCase()])
      ),
    [allArticles]
  );

  const [activeArticleId, setActiveArticleId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number>(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const [visibleArticleIds, setVisibleArticleIds] = useState<Set<string>>(allArticleIds);
  const [noResults, setNoResults] = useState(false);

  // Reset visibility when product/data changes
  useEffect(() => {
    setVisibleArticleIds(allArticleIds);
    setNoResults(false);
    if (searchRef.current) searchRef.current.value = '';
    // Reset DOM display for all cards
    if (contentRef.current) {
      contentRef.current.querySelectorAll('[data-article-id]').forEach((card) => {
        (card as HTMLElement).style.display = '';
      });
    }
  }, [allArticleIds]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Clean up debounce timer on unmount
  useEffect(() => () => window.clearTimeout(debounceRef.current), []);

  // Track article views via IntersectionObserver
  useEffect(() => {
    const viewedIds = new Set<string>();
    const apiUrl = import.meta.env.VITE_APP_API_URL;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = (entry.target as HTMLElement).dataset.articleId;
          if (!id || viewedIds.has(id)) return;
          viewedIds.add(id);
          fetch(`${apiUrl}/help-center/views`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ article_id: id, product }),
          }).catch(() => {});
        });
      },
      { threshold: 0.3 }
    );

    const cards = contentRef.current?.querySelectorAll('[data-article-id]');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [product]);

  const handleArticleClick = useCallback(
    (articleId: string) => {
      setActiveArticleId(articleId);
      const el = document.getElementById(`article-${articleId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (isMobile) {
        setSidebarOpen(false);
      }
    },
    [isMobile]
  );

  const handleSearchChange = useCallback(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const query = (searchRef.current?.value || '').toLowerCase().trim();

      if (contentRef.current) {
        const cards = contentRef.current.querySelectorAll('[data-article-id]');
        const newVisible = new Set<string>();

        cards.forEach((card) => {
          const id = card.getAttribute('data-article-id') || '';
          const match = !query || (articleSearchText.get(id) || '').includes(query);
          (card as HTMLElement).style.display = match ? '' : 'none';
          if (match) newVisible.add(id);
        });

        setVisibleArticleIds(newVisible);
        setNoResults(newVisible.size === 0 && query.length > 0);
      }
    }, 300);
  }, [articleSearchText]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const switchInfo = switchMap[product];

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <div className={classes.headerLeft}>
          <button type="button" className={classes.hamburger} onClick={toggleSidebar}>
            &#9776;
          </button>
          <svg className={classes.headerLogo} width="32" height="32" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="44" height="44" rx="16" fill="rgba(255,255,255,0.2)" />
            <path
              d="M19.76 26.868V22.416C21.272 22.3413 22.4293 22.024 23.232 21.464C24.0533 20.8853 24.464 20.036 24.464 18.916V18.524C24.464 17.6653 24.2027 17.0027 23.68 16.536C23.1573 16.0507 22.4573 15.808 21.58 15.808C20.6467 15.808 19.9 16.0787 19.34 16.62C18.78 17.1613 18.3973 17.852 18.192 18.692L15.532 17.628C15.7 17.0493 15.9427 16.4893 16.26 15.948C16.596 15.4067 17.016 14.9307 17.52 14.52C18.024 14.0907 18.6213 13.7547 19.312 13.512C20.0027 13.2507 20.796 13.12 21.692 13.12C22.6067 13.12 23.4373 13.2507 24.184 13.512C24.9307 13.7733 25.5653 14.1467 26.088 14.632C26.6107 15.0987 27.012 15.6773 27.292 16.368C27.572 17.04 27.712 17.7867 27.712 18.608C27.712 19.448 27.5627 20.204 27.264 20.876C26.984 21.5293 26.6013 22.0893 26.116 22.556C25.6493 23.0227 25.1173 23.4053 24.52 23.704C23.9227 23.984 23.3067 24.18 22.672 24.292V26.868H19.76ZM21.244 33.336C20.5347 33.336 20.012 33.168 19.676 32.832C19.3587 32.4773 19.2 32.0293 19.2 31.488V31.012C19.2 30.4707 19.3587 30.032 19.676 29.696C20.012 29.3413 20.5347 29.164 21.244 29.164C21.9533 29.164 22.4667 29.3413 22.784 29.696C23.12 30.032 23.288 30.4707 23.288 31.012V31.488C23.288 32.0293 23.12 32.4773 22.784 32.832C22.4667 33.168 21.9533 33.336 21.244 33.336Z"
              fill="#FFFFFF"
            />
          </svg>
          <span className={classes.headerTitle}>{titleMap[product]}</span>
        </div>
        <div className={classes.headerRight}>
          <div className={classes.searchWrapper}>
            <span className={classes.searchIcon}>&#128269;</span>
            <input
              ref={searchRef}
              type="text"
              className={classes.searchInput}
              placeholder="Search articles..."
              onInput={handleSearchChange}
            />
          </div>
          <Link to={switchInfo.path} className={classes.switchLink}>
            {switchInfo.label}
          </Link>
        </div>
      </header>

      <div className={classes.body}>
        <HelpCenterSidebar
          collections={data.collections}
          visibleArticleIds={visibleArticleIds}
          activeArticleId={activeArticleId}
          onArticleClick={handleArticleClick}
          open={sidebarOpen}
        />

        {sidebarOpen && isMobile && (
          <div className={classes.overlay} onClick={closeSidebar} role="presentation" />
        )}

        <main className={classes.content} ref={contentRef}>
          {allArticles.map((article) => (
            <HelpCenterArticle
              key={article.id}
              id={article.id}
              title={article.title}
              body={article.body}
            />
          ))}
          {noResults && (
            <div className={classes.noResults}>
              No articles found matching your search.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
